'use client';

import {
  ErrorCode,
  type LoginRequestBody,
  type LoginResponse,
} from '@ahm/api-wrappers-core';
import { local } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui/components/toast';
import { useEffect, useMemo, useState, useTransition } from 'react';
import {
  getAuthSessionFn,
  useLoginMutation,
  useRegisterMutation,
  useSocialLoginMutation,
  type ActivateResponse,
  type AuthSession,
  type LoginWithSocialResponse,
  type RegisterRequestBody,
} from '../api';
import { useUpdatePasscodeMutation } from '../api/update-passcode-fn';
import {
  VerifyLoginErrorCode,
  useVerifyLogin,
  type VerifyLoginRequest,
} from '../api/v4/use-verify-login';
import {
  AuthStep,
  AuthTab,
  LOCAL_STORAGE_KEY__FINGERPRINT,
  LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD,
  LOCAL_STORAGE_KEY__SUGGESTED_CREATE_PASSCODE,
  LoginType,
  PAGE,
} from '../lib/constants';
import {
  useCustomRouter,
  usePathname,
  useSearchParams,
} from '../lib/navigation';
import {
  encrypt,
  parseVietnamesePhoneNumber,
  type SocketClientConfig,
} from '../lib/utils';
import { authLocales, createTranslations, type Locales } from '../locales';
import { useAuthModal } from '../modals/auth-modal-provider';
import { ROUTES } from '../router';
import { useAuthStore } from '../store/auth-store-provider';

export type SocialProvider = 'google' | 'facebook';

type UseAuthHandlers = {
  lang?: keyof Locales;
  socketConfig?: SocketClientConfig;
};

export function useAuthHandlers({ lang = 'vi' }: UseAuthHandlers) {
  const tAuth = useMemo(
    () => createTranslations({ locales: authLocales, lang }),
    [lang]
  );
  const [isPendingTransition, startTransition] = useTransition();
  const { openAuthModal } = useAuthModal();

  const { navigate } = useCustomRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const redirectUri = searchParams.get('redirect_uri');

  const {
    session,
    setSession,
    logout: logoutFn,
    accountsEndpoint,
    apiError,
    setApiError,
    phoneNumber,
    setPhoneNumber,
    activeAuthStep,
    setActiveAuthStep,
    authTabScreen,
    setAuthTabScreen,
    dataActivate,
    setDataActivate,
  } = useAuthStore((state) => state);

  const page = useMemo(() => {
    if (redirectUri) {
      try {
        const url = new URL(redirectUri);
        const origin = url.origin;

        switch (origin) {
          case 'https://appstg.onwheel.io':
          case 'https://appuat.onwheel.io':
          case 'https://app.onwheel.io':
            return PAGE.ONWHEEL;
          case 'https://partnerstg.ahamove.com':
          case 'https://partneruat.ahamove.com':
          case 'https://partner.ahamove.com':
            return PAGE.PARTNER;
          default:
            return PAGE.AHAMOVE;
        }
      } catch (e) {
        console.error('Invalid redirect URL:', redirectUri);
      }
    }

    const currentOrigin =
      typeof window !== 'undefined' ? window.location.origin : '';

    switch (currentOrigin) {
      case 'https://appstg.onwheel.io':
      case 'https://appuat.onwheel.io':
      case 'https://app.onwheel.io':
        return PAGE.ONWHEEL;
      case 'https://partnerstg.ahamove.com':
      case 'https://partneruat.ahamove.com':
      case 'https://partner.ahamove.com':
        return PAGE.PARTNER;
      default:
        return PAGE.AHAMOVE;
    }
  }, [redirectUri]);

  const loginTypeByPage = useMemo(() => {
    switch (page) {
      case PAGE.PARTNER:
        return LoginType.Partner;
      case PAGE.ONWHEEL:
        return LoginType.OnWheel;
      default:
        return LoginType.Web;
    }
  }, [page]);

  const [visitorId] = useState<string | null>(
    local.getItem(LOCAL_STORAGE_KEY__FINGERPRINT)
  );
  const [showModalSelectOtpChannel, setShowModalSelectOtpChannel] =
    useState(false);
  const [loginData, setLoginData] = useState<
    LoginResponse & { isSocket?: boolean }
  >({
    authenticate_type: 'ahamove',
    id: '',
    login_type: '',
    _id: '',
    login_method: '',
  });

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
  });
  const [isForceLoginOTP, setIsForceLoginOTP] = useState<boolean>(false);

  const { mutate: socialLoginMutate, isPending: isSocialLoginPending } =
    useSocialLoginMutation({
      onSuccess(data) {
        if (data.token) {
          handleLoginSuccess(data);
        } else {
          handleSignUpViaSocial(data);
        }
      },
      onError(error) {
        const errMessage =
          error?.response?.data?.description ??
          error.response?.data.title ??
          error.response?.data.message ??
          error.message;

        if (errMessage) {
          toast.error({
            message: errMessage,
            withIcon: true,
          });
        }
      },
    });

  const { mutate: loginMutation, isPending: isLoginPending } = useLoginMutation(
    {
      onSuccess: (res) => {
        handleRequestOTP(res);
      },
      onError: (error, requestBody) => {
        const errorData = error?.response?.data;
        const mobile = requestBody.mobile;

        if (errorData) {
          setPhoneNumber(mobile);
          const title = errorData.title;
          const content = errorData.description || '';
          const countdownInSecond =
            errorData?.extra_params?.countdown_in_second ?? 0;
          const modalProps: Record<string, any> = {
            title,
            content,
            countdownInSecond,
          };
          switch (errorData.code) {
            case ErrorCode.UserNotFound.toString():
              openAuthModal('userNotFound', {
                mobile,
                errorCode: errorData.code,
              });
              break;
            case ErrorCode.BannedUser.toString():
            case ErrorCode.DeactivatedUserWrongPasscode.toString():
            case 'USER_DEACTIVATED':
              openAuthModal('banned', {
                ...modalProps,
                type:
                  ErrorCode.DeactivatedUserWrongPasscode.toString() ===
                  errorData.code
                    ? AuthStep.PassCode
                    : AuthStep.OTP,
                errorCode: errorData.code,
              });
              break;

            default:
              break;
          }
          setApiError(errorData);
        }
      },
    }
  );

  const { mutate: updatePasscode, isPending: isUpdatePasscodePending } =
    useUpdatePasscodeMutation({
      onSuccess: () => {
        handleLoginSuccess(dataActivate!);
      },
      onError: (error) => {
        console.log('error: ', error);
      },
    });
  const { mutate: verifyLogin, isPending: isVerifyLoginPending } =
    useVerifyLogin({
      onSuccess(data) {
        // get local storage phone number when skip create passcode
        const localPhoneNumber = local.getItem(
          LOCAL_STORAGE_KEY__SUGGESTED_CREATE_PASSCODE
        )!;

        if (
          !data?.user?.enable_passcode &&
          localPhoneNumber !== data.user._id &&
          loginTypeByPage === LoginType.Web
        ) {
          setActiveAuthStep(AuthStep.CreatePasscode);
          setDataActivate(data);
        } else {
          handleLoginSuccess(data);
        }
      },
      onError: (error, requestBody) => {
        const errorData = error?.response?.data;
        if (errorData) {
          const errorCode = String(errorData?.code);
          switch (errorCode) {
            case VerifyLoginErrorCode.WrongPasscodeExceededLimitCode.toString():
              setApiError(errorData);
              handleForceLoginOTP(requestBody.mobile);
              break;

            case ErrorCode.UserNotFound.toString():
              break;
            case ErrorCode.BannedUser.toString():
              openAuthModal('banned', {
                type: AuthStep.OTP,
                content: errorData?.description,
                countdownInSecond:
                  errorData?.extra_params?.countdown_in_second ?? 0,
                title: errorData?.title,
              });

              break;
            default:
              setApiError(errorData);
              return;
          }
        }
      },
    });

  const { mutate: register, isPending: isRegisterPending } =
    useRegisterMutation({
      onSuccess: (res) => {
        if (pathname === ROUTES.signUp) {
          const params = {
            id: res._id,
            login_method: res.login_method,
            // login_type: res.login_type,
          };
          const session = encrypt(JSON.stringify(params));

          const encodedSession = encodeURIComponent(session);
          const encodedRedirectUri = redirectUri
            ? encodeURIComponent(redirectUri)
            : '';

          const signInUrl = redirectUri
            ? `${ROUTES.signIn}?sessionId=${encodedSession}&redirect_uri=${encodedRedirectUri}`
            : `${ROUTES.signIn}?sessionId=${encodedSession}`;
          // Next handler in sign-up-form.tsx
          navigate(signInUrl);
        } else {
          setAuthTabScreen(AuthTab.SignIn);
        }
        handleLoginViaPhoneNumber(res._id);
      },
      onError: (error) => {
        const errorData = error?.response?.data;
        if (errorData) {
          setApiError(errorData);
          toast.error({
            title: errorData.title,
            message: errorData.description,
            withIcon: true,
          });
        }
      },
    });

  const getSession = async (): Promise<AuthSession | null> => {
    try {
      const session = await getAuthSessionFn();
      if (session) {
        return session;
      }
      return null;
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const onReSendOTP = (phoneNumber: string) => {
    setApiError(null);
    void handleLoginViaPhoneNumber(phoneNumber, undefined, true);
  };

  const handleLoginSuccess = (
    data: LoginWithSocialResponse | ActivateResponse
  ) => {
    startTransition(() => {
      setApiError(null);
      const newSession: AuthSession = {
        token: data.token,
        refresh_token: data.refresh_token,
      };
      setSession(newSession);
    });
  };

  const handleSignUpViaSocial = (data: LoginWithSocialResponse) => {
    if (pathname === AuthTab.SignIn.toString()) {
      navigate(`${ROUTES.signUp}?name=${data?.name}&&mail=${data.email}`);
    } else {
      setRegisterData({
        name: data?.name ?? '',
        email: data?.email ?? '',
      });
      setActiveAuthStep(AuthStep.PhoneNumber);
      setAuthTabScreen(AuthTab.SignUp);
    }
  };

  const loginViaSocial = (response: any, provider: SocialProvider) => {
    const socialTokenKey = `${provider}_token`;
    const socialToken =
      provider === 'facebook' ? response.accessToken : response.credential;

    if (!socialToken) {
      toast.error({
        message: tAuth('loginMethodNotAvailable'),
        withIcon: true,
      });

      return;
    }

    const body = {
      [socialTokenKey]: socialToken,
      imei: visitorId ?? 'web',
    };
    socialLoginMutate(body);
  };

  const loginViaPhoneNumber = (
    values: {
      mobile: string;
      otp_method?: string;
    },
    resend = false
  ): void => {
    startTransition(() => {
      setApiError(null);
      const phoneNumber = parseVietnamesePhoneNumber(
        values.mobile || loginData._id
      );

      setPhoneNumber(phoneNumber);
      handleLoginViaPhoneNumber(phoneNumber, values.otp_method, resend);
    });
  };

  const handleLoginViaPhoneNumber = (
    mobile: string,
    otp_method?: string,
    resend = false
  ) => {
    const requestBody: LoginRequestBody = {
      mobile,
      type: LoginType.Web,
      resend,
      client_type: 'user',
      imei: visitorId ?? 'web',
      otp_method,
    };

    if (loginTypeByPage && loginTypeByPage !== LoginType.Web) {
      requestBody.login_type = loginTypeByPage;
    }

    loginMutation({ ...requestBody });
  };

  const handleForceLoginOTP = (
    mobile: string,
    otp_method?: string,
    resend = false
  ) => {
    setIsForceLoginOTP(true);
    setShowModalSelectOtpChannel(true);

    // const requestBody: LoginRequestBody = {
    //   mobile: mobile,
    //   type: LoginType.Web,
    //   resend,
    //   client_type: 'user',
    //   imei: visitorId ?? 'web',
    //   force_login_otp: true,
    //   otp_method,
    // };

    // if (loginTypeByPage && loginTypeByPage !== LoginType.Web) {
    //   requestBody.login_type = loginTypeByPage;
    // }
    // setIsForceLoginOTP(true);
    // loginMutation({ ...requestBody });
  };

  const handleRequestOTP = (res: LoginResponse, isSpam = false) => {
    startTransition(() => {
      setLoginData(res);

      if (res?.require_otp_method) {
        setShowModalSelectOtpChannel(true);
        return;
      }
      saveSentActivateCodePayload(res);
      if (res.login_method === 'passcode') {
        setActiveAuthStep(AuthStep.PassCode);
      } else {
        setActiveAuthStep(AuthStep.OTP);
      }
    });
  };

  const saveSentActivateCodePayload = (loginData: LoginResponse) => {
    const payload = {
      time: new Date().getTime(),
      ...loginData,
    };
    local.setItem(
      LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD,
      JSON.stringify(payload)
    );
  };

  const handleUpdatePasscode = (passcode: string) => {
    if (!dataActivate) return;
    updatePasscode({ new_passcode: passcode, token: dataActivate.token });
  };

  const handleActivateUser = (
    activeCode: string,
    data?: LoginResponse & { isSocket?: boolean }
  ) => {
    const mobile = data?.id ?? loginData.id;
    if (!mobile) return;
    // local.setItem(LOCAL_STORAGE_KEY__SENT_ACTIVATE_CODE_PAYLOAD, '');

    const body: VerifyLoginRequest = {
      mobile,
      code: activeCode,
      login_method: data?.login_method ?? loginData.login_method,
      login_type: data?.login_type ?? loginData.login_type,
      imei: visitorId ?? 'web',
      client_type: 'user',
      device_type: 'web',
      push: 'web',
      fcm: true,
    };

    return verifyLogin(body);
  };

  const onRegister = (requestBody: RegisterRequestBody) => {
    register(requestBody);
  };

  const logout = (forceReload = false): void => {
    try {
      logoutFn();

      if (forceReload) {
        window.location.reload();
        return;
      }

      const { origin, pathname, search } = window.location;

      // Skip redirect if already on the accounts URI
      if (origin !== accountsEndpoint) {
        let redirectUrl = `${origin}${pathname}${search}`;
        redirectUrl = encodeURIComponent(redirectUrl);

        navigate(
          `${accountsEndpoint}${ROUTES.signIn}?redirect_uri=${redirectUrl}`
        );
      }
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const handleSkipCreatePasscode = () => {
    if (!dataActivate) return;
    if (handleLoginSuccess) {
      // set local storage phone number when skip create passcode
      local.setItem(
        LOCAL_STORAGE_KEY__SUGGESTED_CREATE_PASSCODE,
        dataActivate.user._id
      );

      handleLoginSuccess(dataActivate);
    }
  };

  useEffect(() => {
    // check if activeAuthStep change, reset apiError
    setApiError(null);
  }, [activeAuthStep]);

  return {
    isPendingTransition,
    isAuthMutationPending:
      isLoginPending ||
      isSocialLoginPending ||
      isUpdatePasscodePending ||
      isVerifyLoginPending ||
      isRegisterPending,
    loginViaPhoneNumber,
    handleUpdatePasscode,
    handleActivateUser,
    register,
    logout,
    session,
    loginViaSocial,
    loginData,
    activeAuthStep,
    setActiveAuthStep,
    authTabScreen,
    setAuthTabScreen,
    apiError,
    phoneNumber,
    setPhoneNumber,
    onReSendOTP,
    handleRequestOTP,
    getSession,
    onRegister,
    registerData,
    page,
    handleLoginSuccess,
    handleForceLoginOTP,
    isForceLoginOTP,
    handleSkipCreatePasscode,
    showModalSelectOtpChannel,
    setShowModalSelectOtpChannel,
  };
}
